"use client";

import { useEffect, useRef, useState } from "react";

import { useRouter } from "next/navigation";

import { ArrowRight, ChevronDown } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { useAuth } from "@/contexts/AuthContext";
import { useBrandDetails } from "@/contexts/BrandDetailsContext";
import { useBrowseMenu } from "@/hooks/useBrowseMenu";
import { resolveBrandBodyCopy } from "@/lib/brand-copy";
import { buildBrandShopPath } from "@/lib/brand-routing";
import { cn } from "@/lib/cn";
import { browseByMenu } from "@/lib/results-navigation";
import {
  getBrandShoppingOptions,
  type BrandShoppingOptionsResponse,
} from "@/lib/shop-agent-client";

import SaveBrandButton from "./SaveBrandButton";

interface BrandHeaderProps {
  brandName: string;
  brandSlug?: string;
}

interface ShopButton {
  label: string;
  rootLabel: string;
  key: keyof Omit<BrandShoppingOptionsResponse, "brand_name">;
}

const SHOP_BUTTONS: ShopButton[] = [
  { label: "Men's", rootLabel: "Menswear", key: "has_menswear" },
  { label: "Women's", rootLabel: "Womenswear", key: "has_womenswear" },
  { label: "Kids", rootLabel: "Kidswear", key: "has_kidswear" },
  { label: "Beauty", rootLabel: "Beauty", key: "has_beauty" },
];

export default function BrandHeader({ brandName, brandSlug }: BrandHeaderProps) {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { roots, isLoading: isBrowseMenuLoading, error: browseMenuError } = useBrowseMenu();
  const { brandDetails, loading: brandLoading } = useBrandDetails();
  const [shoppingOptions, setShoppingOptions] = useState<BrandShoppingOptionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset description expansion state when brand changes
  useEffect(() => {
    setShowFullDescription(false);
  }, [brandName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchShoppingOptions = async () => {
      if (!isAuthenticated || !brandName) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const options = await getBrandShoppingOptions(brandName);
        if (!cancelled) {
          setShoppingOptions(options);
        }
      } catch (err) {
        console.error("Error fetching brand shopping options:", err);
        if (!cancelled) {
          setShoppingOptions(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchShoppingOptions();

    return () => {
      cancelled = true;
    };
  }, [brandName, isAuthenticated]);

  // Navigate to results with browse menu root + brand filter (category buttons)
  const isBrowseMenuReady = !isBrowseMenuLoading && !browseMenuError && roots.length > 0;
  const browseMenuStatusMessage = browseMenuError
    ? "Failed to load categories."
    : "Loading categories...";

  const handleShopClick = (rootLabel: string) => {
    if (!isBrowseMenuReady) return;
    const rootNode =
      roots.find((node) => node.display_name === rootLabel) ||
      roots.find((node) => node.display_name.toLowerCase() === rootLabel.toLowerCase());
    if (!rootNode) {
      console.warn("[BrandHeader] Browse root not found for shop click:", rootLabel);
      return;
    }
    const threadId = uuidv4();
    browseByMenu(router, threadId, {
      facets: rootNode.search_query.facets || [],
      display_path: rootNode.display_path,
      navbar_category: rootNode.display_path[0] || rootNode.display_name,
      brand: brandName,
    });
    setIsDropdownOpen(false);
  };

  // Navigate to results with brand filter only (no category - like M+W mode)
  const handleShopBrandClick = () => {
    router.push(
      buildBrandShopPath({
        brandName,
        brandSlug,
      })
    );
    setIsDropdownOpen(false);
  };

  // Get list of eligible buttons to show.
  // When browse menu is loaded, only include buttons that map to an actual root node
  // so category clicks never silently no-op.
  const eligibleButtons = shoppingOptions
    ? SHOP_BUTTONS.filter((btn) => {
        if (!shoppingOptions[btn.key]) return false;
        if (!isBrowseMenuReady) return true;
        return roots.some(
          (node) => node.display_name === btn.rootLabel || node.display_name === btn.label
        );
      })
    : [];

  const brandDescription = resolveBrandBodyCopy(brandDetails, brandName);

  // Get tagline/subheading
  const tagline =
    brandDetails?.directory_subheading ||
    (brandDetails?.origin_country
      ? `${brandDetails.origin_country} luxury fashion house`
      : "Luxury fashion house");

  // Split description into paragraphs
  const allParagraphs = brandDescription.split("\n\n").filter(Boolean);
  const firstParagraph = allParagraphs[0] || "";
  const hasMultipleParagraphs = allParagraphs.length > 1;

  // Show "See more" if there are multiple paragraphs
  const shouldTruncate = hasMultipleParagraphs;

  // Get visible content - show first paragraph only when collapsed
  const getVisibleContent = (): string => {
    if (showFullDescription || !shouldTruncate) {
      return brandDescription;
    }
    return firstParagraph;
  };

  const visibleContent = getVisibleContent();
  // Split the visible content into paragraphs for rendering
  const visibleParagraphs = visibleContent.split("\n\n").filter(Boolean);

  return (
    <div className="pb-8 md:pb-12">
      {/* Back Button */}
      <button
        onClick={() => router.back()}
        className="mb-8 inline-flex items-center gap-2 text-caption text-ink-secondary transition-colors hover:text-ink"
      >
        <span>←</span>
        <span>Back</span>
      </button>

      {/* Main Content Card */}
      <div className="rounded-3xl bg-white p-6 md:p-10 lg:px-20 lg:py-14">
        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-2 lg:gap-24">
          {/* Left Column - Brand Info */}
          <div className="space-y-6 md:space-y-8">
            {/* Brand Name and Tagline */}
            <div>
              <h1 className="mb-2 text-[32px] font-medium capitalize tracking-tight text-ink md:text-[40px]">
                {brandName.toLowerCase()}
              </h1>
              <p className="text-body capitalize text-ink-muted">{tagline}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Shop Collection - only for authenticated users */}
              {isAuthenticated && (
                <>
                  {loading ? (
                    <div className="h-10 w-36 animate-pulse rounded-full bg-surface-muted" />
                  ) : (
                    /* Shop Collection Dropdown */
                    <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={cn(
                          "flex h-[31px] items-center gap-2 rounded-full bg-ink pl-5 pr-2.5 text-[11px] font-medium text-white transition-all",
                          "hover:bg-black"
                        )}
                      >
                        <span>Shop Collection</span>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform duration-200",
                            isDropdownOpen && "rotate-180"
                          )}
                        />
                      </button>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute left-0 top-full z-50 mt-2 w-[200px] overflow-hidden rounded-xl border border-divider bg-white py-1.5 shadow-lg">
                          {/* All Products Option */}
                          <button
                            onClick={handleShopBrandClick}
                            className="flex w-full items-center justify-between px-4 py-2 text-caption text-ink transition-colors hover:bg-surface-muted"
                          >
                            <span>All Products</span>
                            <ArrowRight className="h-3 w-3 text-ink-muted" />
                          </button>

                          {eligibleButtons.length > 0 && (
                            <div className="my-1 border-t border-divider" />
                          )}

                          {/* Category Options */}
                          {isBrowseMenuReady ? (
                            eligibleButtons.length > 0 ? (
                              eligibleButtons.map((btn) => (
                                <button
                                  key={btn.key}
                                  onClick={() => handleShopClick(btn.rootLabel)}
                                  className="flex w-full items-center justify-between px-4 py-2 text-caption text-ink transition-colors hover:bg-surface-muted"
                                >
                                  <span>{btn.label}</span>
                                  <ArrowRight className="h-3 w-3 text-ink-muted" />
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-caption text-ink-muted">
                                Categories unavailable.
                              </div>
                            )
                          ) : (
                            <div className="px-4 py-2 text-caption text-ink-muted">
                              {browseMenuStatusMessage}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Save Brand - available for all users (shows sign-in modal if not authenticated) */}
              <SaveBrandButton
                brandName={brandName}
                brandSlug={brandSlug ?? brandDetails?.brand_slug ?? undefined}
                brandUuid={brandDetails?.brand_uuid ?? undefined}
                brandDescription={brandDetails?.brand_description ?? undefined}
              />
            </div>

            {/* Brand Metadata Grid */}
            <div className="grid grid-cols-2 gap-x-12 gap-y-4 pt-2 md:gap-y-5 md:pt-10">
              {brandDetails?.origin_country && (
                <div className="space-y-1.5">
                  <p className="text-overline tracking-widest text-ink-faint">ORIGIN</p>
                  <p className="text-body font-medium text-ink">{brandDetails.origin_country}</p>
                </div>
              )}

              {brandDetails?.brand_quality_score != null && (
                <div className="space-y-1.5">
                  <p className="text-overline tracking-widest text-ink-faint">QUALITY SCORE</p>
                  <p className="text-body font-medium text-ink">
                    {brandDetails.brand_quality_score.toFixed(1)} / 6.0
                  </p>
                </div>
              )}

              {brandDetails?.price_tier && (
                <div className="space-y-1.5">
                  <p className="text-overline tracking-widest text-ink-faint">PRICE TIER</p>
                  <p className="text-body font-medium capitalize text-ink">
                    {brandDetails.price_tier.replace(/_/g, " ").toLowerCase()}
                  </p>
                </div>
              )}

              {brandDetails?.target_audience && (
                <div className="space-y-1.5">
                  <p className="text-overline tracking-widest text-ink-faint">SPECIALIZATION</p>
                  <p className="text-body font-medium text-ink">
                    {brandDetails.target_audience.replace(/_/g, "-")}
                  </p>
                </div>
              )}

              {/* Show loading state for metadata */}
              {brandLoading && !brandDetails && (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-2 w-12 animate-pulse rounded bg-surface-muted" />
                      <div className="h-4 w-20 animate-pulse rounded bg-surface-muted" />
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Right Column - Description */}
          <div className="space-y-5 md:space-y-7 md:px-0 lg:pt-5" data-testid="brand-description">
            {brandLoading && !brandDetails ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-4 animate-pulse rounded bg-surface-muted" />
                ))}
              </div>
            ) : (
              <>
                {visibleParagraphs.map((paragraph, index) => (
                  <p key={index} className="text-body leading-loose text-ink-secondary">
                    {paragraph}
                  </p>
                ))}

                {shouldTruncate && (
                  <p className="text-body leading-loose text-ink-secondary">
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="underline decoration-ink-secondary underline-offset-2"
                    >
                      {showFullDescription ? "Show less" : "See more"}
                    </button>
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
